import { Vector2 } from "../../common/vector2.js";
import { formatShortDuration } from "../../common/common_funcs.js";
import { rewardAmountText } from "../../common/reward_format.js";
import { ADVENTURE_REWARD_ICONS } from "../../adventure/adventure_box_rewards.js";
import { ImgID } from "../../image_loader/img_id.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { TournamentService, type TournamentProgressReward } from "../../tournament/tournament_service.js";
import { fillX, progressSector, reachedCount } from "../../tournament/tournament_progress_sectors.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { UIProgressBar } from "../common_ui_elements/ui_progress_bar.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AnchoredTooltip } from "../tooltip/anchored_tooltip.js";
import { DomTooltip } from "../tooltip/dom_tooltip.js";
import { RewardTooltip } from "../tooltip/reward_tooltip.js";
import { TournamentInfoTooltip } from "../tooltip/tournament_info_tooltip.js";
import { AbstractTournamentDialog } from "./abstract_tournament_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { TournamentStandingsList } from "./tournament_standings_list.js";
import { TournamentTitle } from "./tournament_title.js";

interface ProgressPath
{
  /** The current sector's checkpoints and their x on the bar (design px). */
  items: { reward: TournamentProgressReward; x: number }[];
  all_reached: boolean;
  fill_x: number;
  icon_scale: number;
}

export class TournamentDialog extends AbstractTournamentDialog
{
  private static readonly BG_H_MIN = 1733;
  private static readonly BG_H_MAX = 2143;
  private static readonly BG_H_FINISHED_MIN = 1672;
  private static readonly BG_H_FINISHED_MAX = TournamentDialog.BG_H_FINISHED_MIN
    + TournamentDialog.BG_H_MAX - TournamentDialog.BG_H_MIN;
  private static readonly BANNER_W = 1086;
  private static readonly BANNER_H = 268;
  private static readonly BANNER_X = -48;
  private static readonly BANNER_Y = 474;
  private static readonly BANNER_TITLE_CENTER_Y = 565;
  private static readonly BANNER_TITLE_FONT = 60;
  private static readonly BANNER_SUBTITLE_CENTER_Y = 622;
  private static readonly BANNER_SUBTITLE_FONT = 40;
  private static readonly BANNER_TEXT_OUTLINE = 8;
  private static readonly LABEL_Y = 36;
  private static readonly TITLE_OFFSET_Y = -5;
  private static readonly INFO_ART_SIZE = 147;
  private static readonly INFO_ART_PAD = 21;
  private static readonly SUBSTRATE_W = 926;
  private static readonly SUBSTRATE_H = 857;
  private static readonly SUBSTRATE_Y = 710;
  private static readonly SUBSTRATE_Y_FINISHED = 704;
  private static readonly SUBSTRATE_H_FINISHED = 875;
  private static readonly STANDINGS_INSET_Y = 20;
  // The crown badge overhangs the bar's left end to the standings' left edge (x 42);
  // the plate mirrors it on the right (to x 948), the bar ends under the plate.
  private static readonly PROGRESS_X = 132;
  private static readonly PROGRESS_W = 776;
  private static readonly PROGRESS_H = 125;
  private static readonly PROGRESS_Y = 560;
  private static readonly PROGRESS_FILL_INSET_W = 30;
  private static readonly PROGRESS_FILL_INSET_H = 54;
  // Nine-slice caps of the frame and fill art (nine_slice_config.json); the art is 1:1 with design px.
  private static readonly PROGRESS_BG_CAP_W = 40;
  private static readonly PROGRESS_BG_CAP_H = 50;
  private static readonly PROGRESS_FILL_CAP_W = 40;
  private static readonly PROGRESS_FILL_CAP_H = 30;
  private static readonly ORANGE_TAB_W = 485;
  private static readonly ORANGE_TAB_H = 76;
  private static readonly ORANGE_TAB_Y_ABOVE_PROGRESS = 58;
  private static readonly ORANGE_TAB_TEXT_OFFSET_Y = 5;
  private static readonly PROGRESS_REWARD_SIZE = 100;
  private static readonly PROGRESS_COIN_SCALE = 1.35;
  private static readonly PROGRESS_REWARD_COUNT_H = 52;
  private static readonly PROGRESS_REWARD_COUNT_BELOW = 15;
  private static readonly PROGRESS_REWARD_COUNT_PAD = 20;
  /** Checkpoint spacing below which reward icons shrink (5 per sector would overlap the coin piles). */
  private static readonly PROGRESS_SLOT_MIN_STEP = 190;
  // Mock: the chest stands over the bar's right end, the plate sits on the bar
  // under it and hides the bar's end; checkpoints share the crown → chest span evenly.
  private static readonly GRAND_CENTER_X = 948 - 175 / 2;
  private static readonly GRAND_BOX_H = 204;
  private static readonly GRAND_BOX_ASPECT = 460 / 440;
  private static readonly GRAND_BOX_Y_FROM_PROGRESS = -124;
  private static readonly GRAND_PLATE_W = 175;
  private static readonly GRAND_PLATE_H = 82;
  private static readonly GRAND_PLATE_Y_FROM_PROGRESS = 46;
  private static readonly GRAND_PLATE_PAD_X = 14;
  private static readonly GRAND_PLATE_FONT = 34;
  private static readonly CHECK_H = 69;
  private static readonly CHECK_ART = { w: 99, h: 91, left: 9, top: 6, right: 89, bottom: 79 };
  private static readonly CROWN_BG_SIZE = 146;
  private static readonly CROWN_BG_X_FROM_PROGRESS = -90;
  private static readonly CROWN_ICON_SIZE = 160;
  private static readonly CAPSULE_W = 673;
  private static readonly CAPSULE_H = 80;
  private static readonly CAPSULE_BOTTOM_MARGIN = 54;
  /** Only when the dialog has no DOM appear animation to follow (canvas UI backend). */
  private static readonly STANDINGS_FALLBACK_SHOW_MS = 260;

  private standings!: TournamentStandingsList;
  private readonly finished: boolean;
  private readonly path: ProgressPath;
  private standings_show_at = Number.POSITIVE_INFINITY;

  protected override partWidth(): number { return 966; }
  protected override partHeight(): number { return 649; }

  constructor( finished = false )
  {
    super( TournamentDialog.backgroundHeight( finished ), TournamentDialog.TITLE_OFFSET_Y );

    this.finished = finished;
    this.path = TournamentDialog.buildPath();

    this.createLabel();

    if ( finished )
      this.createResultBanner();
    else
    {
      this.createOrangeTab();
      this.createProgressBar();
      this.createProgressRewards();
      this.createCrownBadge();
    }

    this.createSubstrate();
    this.createStandings();

    if ( !finished && TournamentService.current()?.joined === false )
      this.createJoinHint();

    if ( !finished )
      this.createCapsule();

    this.createInfoButton();
  }

  private static backgroundHeight( finished: boolean ): number
  {
    return finished
      ? LayoutScaler.heightFromLayouts( TournamentDialog.BG_H_FINISHED_MIN, TournamentDialog.BG_H_FINISHED_MAX )
      : LayoutScaler.heightFromLayouts( TournamentDialog.BG_H_MIN, TournamentDialog.BG_H_MAX );
  }

  private createResultBanner(): void
  {
    this.addChild( new UIImage(
      this.ctx,
      this.bgX( TournamentDialog.BANNER_X ),
      this.bgY( TournamentDialog.BANNER_Y ),
      LayoutScaler.widthFromDesign( TournamentDialog.BANNER_W ),
      LayoutScaler.heightFromDesign( TournamentDialog.BANNER_H ),
      ImgID.UI_TournamentResultBanner
    ) );

    this.addBannerText( "TOURNAMENT.FINISHED_TITLE", TournamentDialog.BANNER_TITLE_CENTER_Y, TournamentDialog.BANNER_TITLE_FONT );
    this.addBannerText( "TOURNAMENT.RESULT", TournamentDialog.BANNER_SUBTITLE_CENTER_Y, TournamentDialog.BANNER_SUBTITLE_FONT );
  }

  private addBannerText( localization_key: string, center_y: number, font_size: number ): void
  {
    const h = font_size * 2;

    this.addChild( new UITextElement(
      this.ctx,
      this.bgX( 0 ),
      this.bgY( center_y - h / 2 ),
      LayoutScaler.widthFromDesign( AbstractTournamentDialog.BG_W ),
      LayoutScaler.heightFromDesign( h ),
      {
        localization_key,
        font_size: LayoutScaler.textSizeFromDesign( font_size ),
        min_font_size: LayoutScaler.textSizeFromDesign( Math.round( font_size * 0.6 ) ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( TournamentDialog.BANNER_TEXT_OUTLINE ),
        outline_color: ColorScheme.tournament_text_outline,
      }
    ) );
  }

  public name(): string { return "TournamentDialog"; }

  private labelPos(): Vector2
  {
    return new Vector2( this.bgCenterX( TournamentTitle.LABEL_W ), this.bgY( TournamentDialog.LABEL_Y ) );
  }

  private createLabel(): void
  {
    const pos = this.labelPos();

    this.addChild( TournamentTitle.createLabel( this.ctx, pos.x, pos.y ) );
  }

  private substrateY(): number
  {
    return this.finished ? TournamentDialog.SUBSTRATE_Y_FINISHED : TournamentDialog.SUBSTRATE_Y;
  }

  private substrateHeight(): number
  {
    const growth = TournamentDialog.BG_H_MAX - TournamentDialog.BG_H_MIN;
    const base = this.finished ? TournamentDialog.SUBSTRATE_H_FINISHED : TournamentDialog.SUBSTRATE_H;
    return LayoutScaler.heightFromLayouts( base, base + growth );
  }

  private createSubstrate(): void
  {
    this.addChild( new UINineSliceElement(
      this.ctx,
      this.bgCenterX( TournamentDialog.SUBSTRATE_W ),
      this.bgY( this.substrateY() ),
      LayoutScaler.widthFromDesign( TournamentDialog.SUBSTRATE_W ),
      this.substrateHeight(),
      ImgID.UI_TournamentSubstrateMain,
      "tournament_substrate_main"
    ) );
  }

  private createStandings(): void
  {
    this.standings = new TournamentStandingsList(
      this.ctx,
      this.bgCenterX( TournamentStandingsList.ITEM_W ),
      this.bgY( this.substrateY() + TournamentDialog.STANDINGS_INSET_Y ),
      this.substrateHeight() - LayoutScaler.heightFromDesign( TournamentDialog.STANDINGS_INSET_Y ) * 2,
      TournamentService.getStandings()
    );
    this.addChild( this.standings );
    this.standings.updateDOMPosition();
    this.standings.getOverlayHost().style.zIndex = "120";
    // Transparent, not display:none: the rows get rastered while the dialog primes its own
    // first frame, and the list then appears together with it (`onDomAppearStarted`).
    this.standings.getOverlayHost().style.opacity = "0";
  }

  private createJoinHint(): void
  {
    const inset = TournamentDialog.STANDINGS_INSET_Y;
    const width = TournamentDialog.SUBSTRATE_W - inset * 4;

    this.addChild( new UITextElement(
      this.ctx,
      this.bgCenterX( width ),
      this.bgY( this.substrateY() + inset ),
      LayoutScaler.widthFromDesign( width ),
      this.substrateHeight() - LayoutScaler.heightFromDesign( inset ) * 2,
      {
        localization_key: "TOURNAMENT.JOIN_HINT",
        font_size: LayoutScaler.textSizeFromDesign( TournamentDialog.BANNER_SUBTITLE_FONT ),
        min_font_size: LayoutScaler.textSizeFromDesign( Math.round( TournamentDialog.BANNER_SUBTITLE_FONT * 0.6 ) ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( TournamentDialog.BANNER_TEXT_OUTLINE ),
        outline_color: ColorScheme.tournament_text_outline,
      }
    ) );
  }

  public override onDialogOpened(): void
  {
    super.onDialogOpened();
    if ( !DialogManager.useDomAnimations( this ) )
      this.standings_show_at = performance.now() + TournamentDialog.STANDINGS_FALLBACK_SHOW_MS;
  }

  public override onDomAppearStarted( keyframes: Keyframe[], duration_ms: number ): void
  {
    super.onDomAppearStarted( keyframes, duration_ms );
    this.standings_show_at = Number.POSITIVE_INFINITY;
    DialogManager.animateAppearDomLayer( this.standings.getOverlayHost(), this, keyframes, duration_ms );
  }

  protected override onCloseAnimationStarted( to_scale: number, duration_ms: number ): void
  {
    if ( !DialogManager.animateCloseDom( this.standings.getOverlayHost(), this, to_scale, duration_ms ) )
      this.standings.hideElement( duration_ms );
  }

  public override onDialogWillClose(): void
  {
    this.standings.destroy();
    AnchoredTooltip.hide();
    DomTooltip.hide();
  }

  protected override updateBasic( now: number ): void
  {
    super.updateBasic( now );
    this.standings?.update( now );

    if ( now >= this.standings_show_at )
    {
      this.standings_show_at = Number.POSITIVE_INFINITY;
      if ( !this.isClosing() )
        this.standings.showElement( 150 );
    }
  }

  private createOrangeTab(): void
  {
    const w = LayoutScaler.widthFromDesign( TournamentDialog.ORANGE_TAB_W );
    const h = LayoutScaler.heightFromDesign( TournamentDialog.ORANGE_TAB_H );

    const tab = new UINineSliceElement(
      this.ctx,
      this.bgCenterX( TournamentDialog.ORANGE_TAB_W ),
      this.bgY( TournamentDialog.PROGRESS_Y - TournamentDialog.ORANGE_TAB_Y_ABOVE_PROGRESS ),
      w,
      h,
      ImgID.UI_TournamentTopOrangeSubstrate,
      "tournament_top_orange_substrate"
    );

    tab.addChild( new UITextElement(
      this.ctx,
      0,
      LayoutScaler.heightFromDesign( TournamentDialog.ORANGE_TAB_TEXT_OFFSET_Y ),
      w,
      LayoutScaler.heightFromDesign( TournamentDialog.ORANGE_TAB_Y_ABOVE_PROGRESS ),
      {
        localization_key: "TOURNAMENT.REWARD_COLLECTED",
        text_variable_params: [
          String( TournamentService.getRewardsCollected() ),
          String( TournamentService.getRewardsTotal() ),
        ],
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 7 ),
        outline_color: ColorScheme.tournament_capsule_text_outline,
      }
    ), true );

    this.addChild( tab );
  }

  private createProgressBar(): void
  {
    const T = TournamentDialog;
    const bg_cap = { width: LayoutScaler.widthFromDesign( T.PROGRESS_BG_CAP_W ), height: LayoutScaler.heightFromDesign( T.PROGRESS_BG_CAP_H ) };
    const fill_cap = { width: LayoutScaler.widthFromDesign( T.PROGRESS_FILL_CAP_W ), height: LayoutScaler.heightFromDesign( T.PROGRESS_FILL_CAP_H ) };
    const bar = new UIProgressBar(
      this.ctx,
      this.bgX( TournamentDialog.PROGRESS_X ),
      this.bgY( TournamentDialog.PROGRESS_Y ),
      LayoutScaler.widthFromDesign( TournamentDialog.PROGRESS_W ),
      LayoutScaler.heightFromDesign( TournamentDialog.PROGRESS_H ),
      {
        base_img_id: ImgID.UI_TournamentProgressBg,
        fill_img_id: ImgID.UI_TournamentProgressFill,
        base_nine_slice: "tournament_progress_bg",
        fill_nine_slice: "tournament_progress_fill",
        border_width: 0,
        base_corner_sizes: { top_left: { ...bg_cap }, bottom_right: { ...bg_cap } },
        fill_corner_size: fill_cap.width,
        fill_corner_sizes: { top_left: { ...fill_cap }, bottom_right: { ...fill_cap } },
        fill_offset_left: LayoutScaler.widthFromDesign( TournamentDialog.PROGRESS_FILL_INSET_W / 2 ),
        fill_offset_right: LayoutScaler.widthFromDesign( TournamentDialog.PROGRESS_FILL_INSET_W / 2 ),
        fill_height: LayoutScaler.heightFromDesign( TournamentDialog.PROGRESS_H - TournamentDialog.PROGRESS_FILL_INSET_H ),
        initial_progress: this.fillProgress(),
        shrink_fill_to_zero: true,
      }
    );
    bar.setClickable( false );
    this.addChild( bar );
  }

  /**
   * The bar's points left to right: the crown badge, then the current sector's
   * checkpoints spread evenly between it and the grand prize chest (fixed spacing,
   * not proportional to crowns); the chest is not a checkpoint.
   */
  /**
   * Crowns the bar shows: a collected checkpoint always counts as reached, even when
   * debug crowns (they can be negative) put the total below its threshold.
   */
  private static barCrowns( rewards: readonly TournamentProgressReward[] ): number
  {
    const chest = TournamentService.isGrandPrizeClaimed() ? TournamentService.getGrandPrizeCrowns() : 0;
    return Math.max( TournamentService.getCrowns(), chest, ...rewards.filter( reward => reward.claimed ).map( reward => reward.crowns ) );
  }

  private static buildPath(): ProgressPath
  {
    const T = TournamentDialog;
    const rewards = TournamentService.getProgressRewards();
    const crowns = T.barCrowns( rewards );
    const thresholds = rewards.map( reward => reward.crowns );
    const reached = reachedCount( thresholds, crowns );
    const chest = TournamentService.hasGrandPrize() ? TournamentService.getGrandPrizeCrowns() : null;
    const all_reached = reached === rewards.length && ( chest === null ? rewards.length > 0 : crowns >= chest );
    const sector = progressSector( rewards.length, reached, TournamentService.getProgressSectorSize() );

    const sector_rewards = sector.last >= sector.first ? rewards.slice( sector.first, sector.last + 1 ) : [];
    const left_x = T.PROGRESS_X + T.CROWN_BG_X_FROM_PROGRESS + T.CROWN_BG_SIZE / 2;
    const step = ( T.GRAND_CENTER_X - left_x ) / ( sector_rewards.length + 1 );
    const items = sector_rewards.map( ( reward, i ) => ( { reward, x: left_x + step * ( i + 1 ) } ) );

    // Within a group the fill stops at its last checkpoint: reaching it swaps the group; on the
    // last group it goes on to the chest, and the chest reached fills the bar to the end.
    const xs = [ left_x, ...items.map( item => item.x ) ];
    const values = [ sector.first > 0 ? thresholds[sector.first - 1] : 0, ...items.map( item => item.reward.crowns ) ];
    if ( chest !== null && sector.last === rewards.length - 1 )
    {
      xs.push( T.GRAND_CENTER_X );
      values.push( chest );
    }

    return {
      items,
      all_reached,
      fill_x: fillX( xs, values, crowns ),
      icon_scale: Math.min( 1, step / T.PROGRESS_SLOT_MIN_STEP ),
    };
  }

  private createProgressRewards(): void
  {
    const path = this.path;
    const center_y = TournamentDialog.PROGRESS_Y + TournamentDialog.PROGRESS_H / 2;
    const crowns = TournamentDialog.barCrowns( TournamentService.getProgressRewards() );
    for ( const { reward, x } of path.items )
    {
      if ( crowns >= reward.crowns )
        this.createProgressCheck( x, center_y );
      else
        this.createProgressReward( reward, x, center_y, path.icon_scale );
    }

    if ( TournamentService.hasGrandPrize() )
      this.createGrandPrize( path.all_reached );
  }

  /** Fill fraction of the bar's inner track; every checkpoint reached fills it to the end. */
  private fillProgress(): number
  {
    const path = this.path;
    if ( path.all_reached ) return 1;
    const fill_x = TournamentDialog.PROGRESS_X + TournamentDialog.PROGRESS_FILL_INSET_W / 2;
    const fill_w = TournamentDialog.PROGRESS_W - TournamentDialog.PROGRESS_FILL_INSET_W;
    return Math.max( 0, Math.min( 1, ( path.fill_x - fill_x ) / fill_w ) );
  }

  /** The white chest over the bar's right end, the "Grand prize" plate on the bar under it; a tap on either lists what is inside. */
  private createGrandPrize( reached: boolean ): void
  {
    const T = TournamentDialog;
    const box_w = T.GRAND_BOX_H * T.GRAND_BOX_ASPECT;

    const box = new UIImage(
      this.ctx,
      this.bgX( T.GRAND_CENTER_X - box_w / 2 ),
      this.bgY( T.PROGRESS_Y + T.GRAND_BOX_Y_FROM_PROGRESS ),
      LayoutScaler.widthFromDesign( box_w ),
      LayoutScaler.heightFromDesign( T.GRAND_BOX_H ),
      reached ? ImgID.UI_TournamentBoxWhiteOpen : ImgID.UI_TournamentBoxWhite
    );
    const show_contents = (): void =>
    {
      const pos = box.getAbsolutePosition();
      const rect = box.getRect();
      DomTooltip.showRewards( { x: pos.x, y: pos.y, w: rect.getW(), h: rect.getH() },
        TournamentService.getGrandPrizeRewards(), { align_left: true } );
    };
    box.setOnClick( show_contents );
    this.addChild( box );

    const plate_w = LayoutScaler.widthFromDesign( T.GRAND_PLATE_W );
    const plate_h = LayoutScaler.heightFromDesign( T.GRAND_PLATE_H );
    const plate = new UINineSliceElement(
      this.ctx,
      this.bgX( T.GRAND_CENTER_X - T.GRAND_PLATE_W / 2 ),
      this.bgY( T.PROGRESS_Y + T.GRAND_PLATE_Y_FROM_PROGRESS ),
      plate_w,
      plate_h,
      ImgID.UI_TournamentGingerCapsule,
      "tournament_ginger_capsule"
    );
    plate.setOnClick( show_contents );
    const text_pad_x = LayoutScaler.widthFromDesign( T.GRAND_PLATE_PAD_X );
    plate.addChild( new UITextElement(
      this.ctx,
      text_pad_x,
      0,
      plate_w - text_pad_x * 2,
      plate_h,
      {
        localization_key: "GRAND_PRIZE",
        font_size: LayoutScaler.textSizeFromDesign( T.GRAND_PLATE_FONT ),
        min_font_size: LayoutScaler.textSizeFromDesign( Math.round( T.GRAND_PLATE_FONT * 0.7 ) ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 6 ),
        outline_color: ColorScheme.tournament_capsule_text_outline,
      }
    ), true );
    this.addChild( plate );
  }

  private createProgressReward( reward: TournamentProgressReward, center_x: number, center_y: number, scale: number ): void
  {
    // Coins show the shop's coin pile (5:4, like the adventure chest popup), not the single
    // coin, and a size up so the pile reads as big as the other icons.
    const is_coin = reward.type === "coin";
    const base = TournamentDialog.PROGRESS_REWARD_SIZE * scale;
    const size = base * ( is_coin ? TournamentDialog.PROGRESS_COIN_SCALE : 1 );
    const design_w = is_coin ? size * 1.25 : size;
    const icon_w = LayoutScaler.widthFromDesign( design_w );
    const icon_h = LayoutScaler.heightFromDesign( size );

    const icon = new UIImage(
      this.ctx,
      this.bgX( center_x - design_w / 2 ),
      this.bgY( center_y - size / 2 ),
      icon_w,
      icon_h,
      is_coin ? ImgID.UI_ShopBlockImg : ADVENTURE_REWARD_ICONS[reward.type]
    );
    RewardTooltip.attachWithNote( icon, () => ( {
      name: RewardTooltip.nameOfLegacy( reward.type ),
      note: LocalizationManager.tWithParams( "TOURNAMENT.REQUIRED", [ String( reward.crowns ) ] ),
      note_icon: ImgID.UI_TournamentMiniCrown,
    } ) );

    // Every count sits at the coin pile's level (the tallest icon), so they line up along the bar.
    const coin_size = base * TournamentDialog.PROGRESS_COIN_SCALE;
    const count_h = LayoutScaler.heightFromDesign( TournamentDialog.PROGRESS_REWARD_COUNT_H );
    // The box grows around the same centre: a 38 px font with a 10 px outline is taller than the 52 px slot.
    const count_pad = LayoutScaler.heightFromDesign( TournamentDialog.PROGRESS_REWARD_COUNT_PAD );
    const count_y = LayoutScaler.heightFromDesign( ( coin_size + size ) / 2 ) - count_h
      + LayoutScaler.yFromDesign( TournamentDialog.PROGRESS_REWARD_COUNT_BELOW ) - count_pad;
    const count_box_h = count_h + count_pad * 2;
    const count_style = {
      text: reward.type === "coin" ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
      font_size: LayoutScaler.textSizeFromDesign( 38 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      auto_wrap: false,
    };
    this.addChild( icon );

    // The count hangs below the icon over the bar's bottom edge; as the icon's own children
    // that part went under the bar, so it is a separate element on top of it.
    const count_x = this.bgX( center_x - design_w / 2 );
    const count_top = this.bgY( center_y - size / 2 ) + count_y;
    // One element with its own outline: a separate outline copy shifted down left the glyph tops bare.
    const count = new UITextElement( this.ctx, count_x, count_top, icon_w, count_box_h, {
      ...count_style,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.booster_dialog_quantity_outline,
    } );
    count.setClickable( false );
    this.addChild( count );
  }

  private createProgressCheck( center_x: number, center_y: number ): void
  {
    const art = TournamentDialog.CHECK_ART;
    const scale = TournamentDialog.CHECK_H / ( art.bottom - art.top );

    const check = new UIImage(
      this.ctx,
      this.bgX( center_x - ( art.left + art.right ) / 2 * scale ),
      this.bgY( center_y - ( art.top + art.bottom ) / 2 * scale ),
      LayoutScaler.widthFromDesign( art.w * scale ),
      LayoutScaler.heightFromDesign( art.h * scale ),
      ImgID.UI_TournamentCheck
    );
    check.setClickable( false );
    this.addChild( check );
  }

  private createCrownBadge(): void
  {
    const progress_x = TournamentDialog.PROGRESS_X;
    const bg_size = LayoutScaler.widthFromDesign( TournamentDialog.CROWN_BG_SIZE );

    const crown_bg = new UIImage(
      this.ctx,
      this.bgX( progress_x + TournamentDialog.CROWN_BG_X_FROM_PROGRESS ),
      this.bgY( TournamentDialog.PROGRESS_Y + ( TournamentDialog.PROGRESS_H - TournamentDialog.CROWN_BG_SIZE ) / 2 ),
      bg_size,
      LayoutScaler.heightFromDesign( TournamentDialog.CROWN_BG_SIZE ),
      ImgID.UI_TournamentCrownBg
    );

    const icon_size = LayoutScaler.widthFromDesign( TournamentDialog.CROWN_ICON_SIZE );
    const icon_offset = ( bg_size - icon_size ) / 2;

    crown_bg.addChild( new UIImage(
      this.ctx,
      icon_offset,
      icon_offset,
      icon_size,
      LayoutScaler.heightFromDesign( TournamentDialog.CROWN_ICON_SIZE ),
      ImgID.UI_TournamentCrownIcon
    ), true );

    RewardTooltip.attachName( crown_bg, () => LocalizationManager.t( "TURNAMENT_SCORE" ) );

    this.addChild( crown_bg );
  }

  private createCapsule(): void
  {
    const w = LayoutScaler.widthFromDesign( TournamentDialog.CAPSULE_W );
    const h = LayoutScaler.heightFromDesign( TournamentDialog.CAPSULE_H );
    const text_pad_x = LayoutScaler.widthFromDesign( 30 );

    const capsule = new UINineSliceElement(
      this.ctx,
      this.bgCenterX( TournamentDialog.CAPSULE_W ),
      this.bgBottomY( TournamentDialog.CAPSULE_BOTTOM_MARGIN + TournamentDialog.CAPSULE_H ),
      w,
      h,
      ImgID.UI_TournamentBlueCapsule,
      "tournament_blue_capsule"
    );

    capsule.addChild( new UITextElement(
      this.ctx,
      text_pad_x,
      0,
      w - text_pad_x * 2,
      h,
      {
        localization_key: "TOURNAMENT.TIME_LEFT",
        text_variable_params: [ formatShortDuration( TournamentService.getMsUntilEnd() ) ],
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.tournament_text_outline,
      }
    ), true );

    this.addChild( capsule );
  }

  private createInfoButton(): void
  {
    const button = this.addCornerButton(
      -AbstractTournamentDialog.CORNER_BTN_OVERHANG_X,
      ImgID.UI_TournamentInfoBtn,
      TournamentDialog.INFO_ART_SIZE,
      TournamentDialog.INFO_ART_PAD
    );
    button.setOnClick( () => TournamentInfoTooltip.show( button ) );
  }
}
